import google from 'google';
import { consoleInfo, getNodes } from '@gravityforms/utils';

/**
 * @function fillInAddress
 * @description The curried callback for when a place is selected from autocomplete.
 *
 * @param {object}  autocomplete The autocomplete instance.
 * @param {Element} address      The address field for autocomplete.
 *
 * @return {(function(): void)} The callback for `place_selected` event.
 */
const fillInAddress = ( autocomplete, address ) => () => {
	const placeSelected = autocomplete.getPlace();

	const addressLine1Input = getNodes( '.address_line_1 input', false, address, true )[ 0 ];
	const addressCityInput = getNodes( '.address_city input', false, address, true )[ 0 ];
	const addressStateInput = getNodes( '.address_state input', false, address, true )[ 0 ];
	const addressStateSelect = getNodes( '.address_state select', false, address, true )[ 0 ];
	const addressZipInput = getNodes( '.address_zip input', false, address, true )[ 0 ];
	const addressCountrySelect = getNodes( '.address_country select', false, address, true )[ 0 ];
	const addressLatInput = getNodes( 'input[name$="geolocation_latitude"]', false, address, true )[ 0 ];
	const addressLngInput = getNodes( 'input[name$="geolocation_longitude"]', false, address, true )[ 0 ];

	const addressLine1Components = [];
	const postalCodeComponents = [];

	// Return early if address components is not an array.
	if ( ! Array.isArray( placeSelected.address_components ) ) {
		return;
	}

	for ( const component of placeSelected.address_components ) {
		const componentType = component.types[ 0 ];

		switch ( componentType ) {
			case 'street_number':
			case 'route':
				addressLine1Components.push( component.long_name );
				break;
			case 'postal_code_prefix':
			case 'postal_code':
			case 'postal_code_suffix':
				postalCodeComponents.push( component.long_name );
				break;
			case 'locality':
				// If no city input, break early.
				if ( ! addressCityInput ) {
					break;
				}
				addressCityInput.value = component.long_name;
				break;
			case 'administrative_area_level_1':
				// If no state input or select, break early.
				if ( ! addressStateInput && ! addressStateSelect ) {
					break;
				}

				if ( addressStateInput ) {
					addressStateInput.value = component.short_name;
				} else if ( addressStateSelect ) {
					addressStateSelect.value = component.long_name;
				}
				break;
			case 'country':
				// If no country select, break early.
				if ( ! addressCountrySelect ) {
					break;
				}

				addressCountrySelect.value = component.long_name;
				break;
		}
	}

	if ( addressLine1Input ) {
		addressLine1Input.value = addressLine1Components.join( ' ' );
	}
	if ( addressZipInput ) {
		addressZipInput.value = postalCodeComponents.join( ' ' );
	}
	if (
		addressLatInput &&
		placeSelected?.geometry?.location?.lat &&
		typeof placeSelected.geometry.location.lat === 'function'
	) {
		addressLatInput.value = placeSelected.geometry.location.lat();
	}
	if (
		addressLngInput &&
		placeSelected?.geometry?.location?.lng &&
		typeof placeSelected.geometry.location.lng === 'function'
	) {
		addressLngInput.value = placeSelected.geometry.location.lng();
	}
};

/**
 * @function enableAutocomplete
 * @description Set up Google autcomplete for each address field.
 *
 */
const enableAutocomplete = () => {
	const addressFields = getNodes( '.gfield[data-js="geolocation-enabled"]', false, document, true );

	// If no address fields with geolocation, return early.
	if ( ! addressFields.length ) {
		return;
	}

	addressFields.forEach( ( address ) => {
		const addressLine1Input = getNodes( '.address_line_1 input', false, address, true )[ 0 ];

		// If address line 1 input is not found, return early.
		if ( ! addressLine1Input ) {
			return;
		}

		// Set up autocomplete on address field.
		const autocomplete = new google.maps.places.Autocomplete( addressLine1Input, {
			fields: [ 'address_components', 'geometry' ],
			types: [ 'address' ],
		} );

		autocomplete.addListener( 'place_changed', fillInAddress( autocomplete, address ) );
	} );
};

/**
 * @function init
 * @description Initialize Google Places functionality.
 *
 */
const init = () => {
	enableAutocomplete();

	consoleInfo( 'Gravity Forms Google Places: Initialized.' );
};

export default init;
